import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, money } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, Route, Gauge, DollarSign } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const empty = () => ({ date: today(), driver: "", vehicle: "", miles: "", rate: 0.67, notes: "" });

export default function Mileage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());

  const load = () => api.get("/mileage").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.miles || Number(form.miles) <= 0) return toast.error("Enter miles driven");
    try {
      await api.post("/mileage", { ...form, miles: Number(form.miles) || 0, rate: Number(form.rate) || 0 });
      toast.success("Mileage logged & added to expenses");
      setOpen(false); setForm(empty()); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/mileage/${id}`); toast.success("Deleted"); load(); };

  const month = today().slice(0, 7);
  const monthItems = items.filter((m) => (m.date || "").startsWith(month));
  const totalMiles = monthItems.reduce((a, m) => a + (m.miles || 0), 0);
  const totalCost = monthItems.reduce((a, m) => a + (m.cost || 0), 0);

  return (
    <div className="space-y-6" data-testid="mileage-page">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="p-5"><div className="flex items-center justify-between"><span className="overline text-muted-foreground">Miles (this month)</span><Gauge className="h-4 w-4 text-accent" /></div><div className="font-display text-2xl font-black mt-2 tabular-nums">{totalMiles.toLocaleString()}</div></Card>
        <Card className="p-5"><div className="flex items-center justify-between"><span className="overline text-muted-foreground">Mileage Cost</span><DollarSign className="h-4 w-4 text-secondary" /></div><div className="font-display text-2xl font-black mt-2 tabular-nums">{money(totalCost)}</div></Card>
        <Card className="p-5"><div className="flex items-center justify-between"><span className="overline text-muted-foreground">Logs</span><Route className="h-4 w-4 text-primary" /></div><div className="font-display text-2xl font-black mt-2 tabular-nums">{monthItems.length}</div></Card>
      </div>

      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Log daily mileage. Each entry auto-posts a "Mileage" expense (miles × rate).</p>
        <Button data-testid="add-mileage-btn" onClick={() => { setForm(empty()); setOpen(true); }}><Plus className="h-4 w-4 mr-1.5" /> Log Mileage</Button>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-muted/50"><TableHead>Date</TableHead><TableHead>Driver</TableHead><TableHead>Vehicle</TableHead><TableHead className="text-right">Miles</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Cost</TableHead><TableHead>Notes</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">No mileage logged yet.</TableCell></TableRow>
            ) : items.map((m) => (
              <TableRow key={m.id} data-testid="mileage-row" className="hover:bg-muted/30">
                <TableCell className="text-sm">{m.date}</TableCell>
                <TableCell>{m.driver || "—"}</TableCell>
                <TableCell className="text-muted-foreground">{m.vehicle || "—"}</TableCell>
                <TableCell className="text-right tabular-nums">{m.miles}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">${m.rate}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{money(m.cost)}</TableCell>
                <TableCell className="text-muted-foreground text-sm max-w-[14rem] truncate">{m.notes}</TableCell>
                <TableCell className="text-right"><button data-testid="delete-mileage-btn" onClick={() => remove(m.id)} className="p-2 rounded-md hover:bg-destructive/10"><Trash2 className="h-4 w-4 text-destructive" /></button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Log Mileage</DialogTitle><DialogDescription>Records the trip and posts a Mileage expense automatically.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="mileage-date" /></div>
            <div className="space-y-1.5"><Label>Miles *</Label><Input type="number" value={form.miles} onChange={(e) => setForm({ ...form, miles: e.target.value })} data-testid="mileage-miles" /></div>
            <div className="space-y-1.5"><Label>Driver</Label><Input value={form.driver} onChange={(e) => setForm({ ...form, driver: e.target.value })} placeholder="Auto = you" /></div>
            <div className="space-y-1.5"><Label>Vehicle</Label><Input value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })} placeholder="Truck 1" /></div>
            <div className="space-y-1.5"><Label>Rate ($/mi)</Label><Input type="number" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} data-testid="mileage-rate" /></div>
            <div className="col-span-2 space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-mileage-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
